import React from "react";
import {BaseComponent, ch, IBaseComponent} from "@renta-apps/athenaeum-react-common";
import {
    Button,
    ButtonType,
    CellAction,
    CellModel,
    Checkbox,
    ColumnActionType,
    ColumnDefinition,
    ColumnType,
    DropdownAlign,
    Grid,
    GridHoveringType,
    GridModel,
    GridOddType,
    IconSize,
    Inline,
    JustifyContent,
    RowModel,
    TextInput,
    ToolbarContainer,
    ToolbarRow
} from "@renta-apps/athenaeum-react-components";
import ToolsUtility from "@/helpers/ToolsUtility";
import RentaToolsConstants from "@/helpers/RentaToolsConstants";
import MassProductReportDefinition from "@/pages/Models/MassProductReportDefinition";
import MassProductReportDefinitionDetails from "@/pages/MassProductsPage/MassProductsMappingsData/MassProductReportDefinitionDetails/MassProductReportDefinitionDetails";
import MassProduct from "@/models/server/MassProduct";
import ReportDefinitionItem from "@/pages/Models/ReportDefinitionItem";
import {ActionType, ReportDefinitionType, ReportItemType} from "@/models/Enums";
import CheckItem from "@/pages/Models/CheckItem";
import SaveReportDefinitionRequest from "@/models/server/requests/SaveReportDefinitionRequest";
import SaveMassProductReportDefinitionResponse from "@/models/server/responses/SaveMassProductReportDefinitionResponse";
import {IPagedList, SortDirection, Utility} from "@renta-apps/athenaeum-toolkit";
import ListMassProductReportDefinitionsRequest from "@/models/server/requests/ListMassProductReportDefinitionsRequest";
import GetMassProductsRequest from "@/models/server/requests/GetMassProductsRequest";
import DeleteReportDefinitionResponse from "@/models/server/responses/DeleteReportDefinitionResponse";
import Comparator from "@/helpers/Comparator";
import Debouncer from "@/helpers/Debouncer";
import MassProductsController from "@/pages/MassProductsController";
import RentaToolsController from "@/pages/RentaToolsController";
import Localizer from "@/localization/Localizer";

import styles from "@/pages/MassProductsPage/MassProductsMappingsData/MassProductsMappingsData.module.scss";

interface IMassProductsMappingsDataProps  {
    reloadMassProductsData(sender: IBaseComponent): Promise<void>;
}

interface IMassProductsMappingsDataState {
    allMassProducts: MassProduct[];
    showDeleted: boolean;
    searchTerm: string | null;
}

export default class MassProductsMappingsData extends BaseComponent<IMassProductsMappingsDataProps, IMassProductsMappingsDataState> {

    state: IMassProductsMappingsDataState = {
        allMassProducts: [],
        showDeleted: false,
        searchTerm: null,
    };

    private readonly _massProductReportDefinitionsGridRef: React.RefObject<Grid<MassProductReportDefinition>> = React.createRef();
    private readonly _searchDebouncer: Debouncer = new Debouncer();

    private get massProductReportDefinitionsGrid(): GridModel<MassProductReportDefinition> {
        return this._massProductReportDefinitionsGridRef.current!.model;
    }

    private get newRowAlreadyExists(): boolean {
        return (this.massProductReportDefinitionsGrid.rows.some(row => !row.deleted && !row.model.id));
    }

    private readonly _massProductReportDefinitionsColumns: ColumnDefinition[] = [
        {
            name: "details",
            header: "#",
            accessor: "#",
            minWidth: 50,
            noWrap: true,
            className: "grey",
            actions: [
                {
                    title: "Details",
                    type: ColumnActionType.Details,
                    right: false,
                    callback: async (cell) => await this.toggleDetailsAsync(cell)
                }
            ],
            init: (cell) => this.initDetailsCell(cell),
        },
        {
            header: Localizer.massProductsMappingsDataReportDefinitionNameLanguageItemName,
            name: "name",
            accessor: nameof.full<MassProductReportDefinition>(item => item.name),
            minWidth: 250,
            type: ColumnType.Text,
            editable: true,
            sorting: true,
            noWrap: true,
            reRenderRow: true,
            settings: {
                required: true,
                maxLength: RentaToolsConstants.dbKeyLength,
            },
        },
        {
            header: Localizer.massProductsMappingsDataStepNameLanguageItemName,
            name: "stepName",
            accessor: nameof.full<MassProductReportDefinition>(item => item.items[0].name),
            type: ColumnType.Text,
            minWidth: 200,
            noWrap: false,
            settings: {
                required: false,
                maxLength: RentaToolsConstants.dbKeyLength,
            }
        },
        {
            header: Localizer.massProductsMappingsDataStepTitleLanguageItemName,
            accessor: nameof.full<MassProductReportDefinition>(item => item.items[0].title),
            type: ColumnType.Text,
            minWidth: 150,
            noWrap: false,
            visible: false,
            settings: {
                maxLength: RentaToolsConstants.dbKeyLength,
            }
        },
        {
            header: Localizer.massProductsMappingsDataManualLanguageItemName,
            accessor: nameof.full<MassProductReportDefinition>(item => item.items[0].manual),
            type: ColumnType.Text,
            minWidth: 200,
            noWrap: false,
            settings: {
                maxLength: RentaToolsConstants.maximumCommentLength,
            }
        },
        {
            header: Localizer.massProductsMappingsDataProductGroupsLanguageItemName,
            name: "massProducts",
            accessor: nameof.full<MassProductReportDefinition>(item => item.massProducts),
            type: ColumnType.Dropdown,
            editable: true,
            minWidth: 150,
            settings: {
                fetchItems: async (cell: CellModel<MassProductReportDefinition>) => this.listAvailableMassProducts(cell),
                align: DropdownAlign.Left,
                multiple: true,
                groupSelected: true,
                nothingSelectedText: Localizer.componentDropdownNothingSelectedLanguageItemName,
            },
            init: (cell: CellModel<MassProductReportDefinition>) => cell.reloadAsync(),
            callback: async (cell) => await this.onChangeProductGroupsAsync(cell)
        },
        {
            name: "actions",
            stretch: true,
            minWidth: "6rem",
            removable: false,
            editable: true,
            init: (cell: CellModel<MassProductReportDefinition>) => this.initMassProductReportDefinitionOperationsAsync(cell),
            actions: [
                {
                    name: "save",
                    title: Localizer.genericSaveLanguageItemName,
                    icon: "far save",
                    type: ActionType.Create,
                    callback: async (cell, action) => await this.processMassProductReportDefinitionOperationAsync(cell, action)
                },
                {
                    name: "cancel",
                    title: Localizer.genericCancelLanguageItemName,
                    icon: "far ban",
                    type: ActionType.Delete,
                    right: true,
                    callback: async (cell, action) => await this.processMassProductReportDefinitionOperationAsync(cell, action)
                },
                {
                    name: "delete",
                    title: Localizer.genericRemoveLanguageItemName,
                    icon: "far trash-alt",
                    type: ActionType.Delete,
                    right: true,
                    confirm: (cell) => this.getDeleteConfirmation(cell),
                    callback: async (cell, action) => await this.processMassProductReportDefinitionOperationAsync(cell, action)
                },
                {
                    name: "restore",
                    title: Localizer.genericRestoreLanguageItemName,
                    icon: "far undo-alt",
                    type: ActionType.Create,
                    right: true,
                    callback: async (cell, action) => await this.processMassProductReportDefinitionOperationAsync(cell, action)
                },
                {
                    name: "copy",
                    title: Localizer.genericCopyLanguageItemName,
                    icon: "far copy",
                    type: ActionType.Default,
                    callback: async (cell, action) => this.processMassProductReportDefinitionOperationAsync(cell, action)
                },
            ]
        }
    ];

    public getTitle(): string {
        return Localizer.massProductsMappingsDataTitle;
    }

    private listAvailableMassProducts(cell: CellModel<MassProductReportDefinition>): MassProduct[] {
        const currentMassProductsIds: string[] = cell.model.massProducts?.map(item => item.id);

        const selectedMassProductsIds: string[] = cell.grid.rows
            .selectMany(item => (item.model.massProducts ?? [])
                .map(massProduct => massProduct.id));

        const massProductsIdsToExclude: string[] = selectedMassProductsIds
            .where(item => !currentMassProductsIds?.includes(item));

        return this
            .state
            .allMassProducts
            .where(item => !massProductsIdsToExclude.includes(item.id));
    }

    private initDetailsCell(cell: CellModel<MassProductReportDefinition>): void {
        const showDetails: CellAction<MassProductReportDefinition> = cell.actions[0];
        showDetails.visible = true;
    }

    private getDeleteConfirmation(cell: CellModel<MassProductReportDefinition>): string {
        const model: MassProductReportDefinition = cell.model;
        const isNew: boolean = !model.id;
        return (isNew)
            ? null as any
            : Utility.format(Localizer.massProductsMappingsDataDeleteConfirmation, model.name);
    }

    private isValid(massProductReportDefinition: MassProductReportDefinition): boolean {
        return (
            (!ToolsUtility.isNullOrEmpty(massProductReportDefinition.name)) &&
            (
                (massProductReportDefinition.items.length > 0) &&
                (
                    (massProductReportDefinition.items[0].checks != null) &&
                    (massProductReportDefinition.items[0].checks.length > 0) &&
                    (massProductReportDefinition.items[0].checks.every(c => !ToolsUtility.isNullOrEmpty(c.name)))
                )
            )
        );
    }

    private initRow(row: RowModel<MassProductReportDefinition>): void {
        const model: MassProductReportDefinition = row.model;

        const isNew: boolean = (ToolsUtility.isNullOrEmpty(model.id));
        const isValid: boolean = this.isValid(model);

        const innerTableDataModified: boolean = this.isCheckGridModified(row);

        row.className = styles.row;
        row.className = this.cssIf(row.className, !isValid, "bg-declined");
        row.className = this.cssIf(row.className, isNew, "bg-approved");

        row.readonly = (innerTableDataModified) && (!isNew);
    }

    private async onInnerGridChangeAsync(row: RowModel<MassProductReportDefinition>): Promise<void> {
        await row.reRenderAsync();
    }

    private async toggleDetailsAsync(cell: CellModel<MassProductReportDefinition>): Promise<void> {
        const spannedRows: RowModel<MassProductReportDefinition>[] = cell.spannedRows;
        const rowToExpand: RowModel<MassProductReportDefinition> = spannedRows[spannedRows.length - 1];
        await rowToExpand.toggleAsync();
    }

    private async onChangeProductGroupsAsync(cell: CellModel<MassProductReportDefinition>): Promise<void> {
        const newData: MassProduct[] = cell.value as MassProduct[];
        const initialData: MassProduct[] = cell.initialValue as MassProduct[];
        const isEqual: boolean = Comparator.isEqual(newData, initialData);

        if (isEqual) {
            cell.save();
            return;
        }

        const cellsToReload: CellModel<MassProductReportDefinition>[] = cell
            .grid
            .rows
            .where(row => row.index != cell.row.index)
            .selectMany(item => item.cells.where(c => c.column.name == cell.column.name));

        await cellsToReload.forEachAsync(cell => cell.reloadAsync());
    }

    private getCheckGrid(row: RowModel<MassProductReportDefinition>): GridModel<CheckItem> | null {
        const ref = row.tag as React.RefObject<MassProductReportDefinitionDetails>;
        return ref?.current?.checkItemsGrid ?? null;
    }

    private isCheckGridModified(row: RowModel<MassProductReportDefinition>): boolean {
        const checksGrid: GridModel<CheckItem> | null = this.getCheckGrid(row);
        return (
            (checksGrid != null) && (
                (checksGrid.modified) ||
                (checksGrid.rows.some(row => row.deleted)) ||
                (checksGrid.items.some(item => !item.id))
            )
        );
    }

    private async initMassProductReportDefinitionOperationsAsync(cell: CellModel<MassProductReportDefinition>): Promise<void> {
        const model: MassProductReportDefinition = cell.model;

        const innerTableDataModified: boolean = this.isCheckGridModified(cell.row);

        const modified: boolean = cell.row.modified || innerTableDataModified;
        const deleted: boolean = cell.row.deleted;
        const isValid: boolean = this.isValid(model);
        const isNew: boolean = !model.id;
        const newRowExists: boolean = this.newRowAlreadyExists;

        const saveAction: CellAction<MassProductReportDefinition> = cell.actions[0];
        const cancelAction: CellAction<MassProductReportDefinition> = cell.actions[1];
        const deleteAction: CellAction<MassProductReportDefinition> = cell.actions[2];
        const restoreAction: CellAction<MassProductReportDefinition> = cell.actions[3];
        const copyAction: CellAction<MassProductReportDefinition> = cell.actions[4];

        saveAction.visible = (modified) && (isValid);
        cancelAction.visible = (modified) && (!isNew);
        deleteAction.visible = (!deleted) && ((!modified) || (isNew));
        restoreAction.visible = (deleted);
        copyAction.visible = (!deleted) && (!isNew) && (!modified) && (isValid) && (!newRowExists);
    }

    private async processMassProductReportDefinitionOperationAsync(cell: CellModel<MassProductReportDefinition>, action: CellAction<MassProductReportDefinition>): Promise<void> {

        await ch.hideAlertAsync();

        const model: MassProductReportDefinition = cell.model;
        const isNew: boolean = (!model.id);

        if (action.action.name === "save") {

            const innerGrid: GridModel<CheckItem> | null = this.getCheckGrid(cell.row);

            // Remove from the model the checks which are marked as deleted:
            const checksToRemove: CheckItem[] = innerGrid?.rows.where(item => item.deleted).map(item => item.model) ?? [];
            model.items[0].checks?.remove(checksToRemove);

            const request = new SaveReportDefinitionRequest();
            request.reportDefinitionId = (!isNew) ? model.id : null;
            request.name = model.name;
            request.items = [model.items[0]];
            request.reportDefinitionType = ReportDefinitionType.MassProduct;
            request.massProducts = model.massProducts;

            const response: SaveMassProductReportDefinitionResponse = await this.postAsync("api/reportDefinition/saveMassProductReportDefinition", request);
            let message: string = "";
            let alertType: 'error' | 'warning' | 'success';

            if (!response.massProductReportDefinitionAlreadyExists) {

                if (isNew) {

                    message += Localizer.get(Localizer.massProductsMappingsDataReportDefinitionCreated, model.name);
                    alertType = "success";

                } else {

                    message += Localizer.get(Localizer.massProductsMappingsDataReportDefinitionUpdated, model.name);
                    alertType = "success";
                }

                if (response.mappingSucceed == true) {
                    message += "\n";

                    // Mappings partially updated.
                    if (response.notMappedMassProducts && response.notMappedMassProducts.length > 0) {

                        const notMappedProductGroups: string = response.notMappedMassProducts
                            .map(item => `"${item.productGroupId} (${item.productType})"`)
                            .join(", ");

                        message += Localizer.get(Localizer.massProductsMappingsDataMappingsPartiallyUpdated, notMappedProductGroups);
                        alertType = "warning";
                    }
                    // All mappings updated.
                    else {

                        message += Localizer.massProductsMappingsDataMappingsSuccessfullyUpdated;
                        alertType = "success";
                    }
                    // None mapping updated.
                } else if (response.mappingSucceed == false) {
                    message += "\n";

                    message += Localizer.massProductsMappingsDataMappingsUpdateFailed;
                    alertType = "error";
                }
            } else {

                await ch.alertErrorAsync(Utility.format(Localizer.massProductsMappingsDataMassProductReportDefinitionAlreadyExists, model.name), true);
                return;
            }

            switch (alertType) {
                case "success":
                    await ch.alertMessageAsync(message, true);
                    break;

                case "warning":
                    await ch.alertWarningAsync(message, false);
                    break;

                case "error":
                    await ch.alertErrorAsync(message, false);
            }

            cell.row.model = response.massProductReportDefinition;

            await innerGrid?.reloadAsync();

            await cell.row.bindAsync();

            await cell.row.reRenderAsync();

        } else if (action.action.name === "cancel") {

            await cell.row.cancelAsync();

            await this.handleInnerGridChangesAsync(cell.row);

        } else if (action.action.name === "delete") {

            model.isDeleted = true;
            model.massProducts = [];

            if (isNew) {

                await cell.grid.deleteAsync(cell.row.index);

            } else {

                const response: DeleteReportDefinitionResponse = await this.postAsync("api/reportDefinition/deleteReportDefinition", model.id);

                if (response.removedPermanently) {

                    await cell.grid.deleteAsync(cell.row.index);

                    await ch.alertMessageAsync(Localizer.reportDefinitionsPageDeletePermanently);

                } else {

                    await cell.row.setDeletedAsync(true);

                    await ch.alertMessageAsync(Localizer.reportDefinitionsPageDelete);
                }
            }

        } else if (action.action.name === "restore") {

            const restoreOnServer: boolean = !isNew;

            if (restoreOnServer) {

                await this.postAsync("api/reportDefinition/restoreReportDefinition", model.id);

                model.isDeleted = false;

                await ch.alertMessageAsync(Localizer.reportDefinitionsPageRestore);
            }

            await cell.row.setDeletedAsync(false);

        } else if (action.action.name === "copy") {

            await this.duplicateMassProductReportDefinitionAsync(cell);
        }

        await this.props.reloadMassProductsData(this);
    }

    private async addMassProductReportDefinitionAsync(): Promise<void> {
        if (!this.newRowAlreadyExists) {
            await this.collapseAllRowsAsync();

            const massProductReportDefinition = new MassProductReportDefinition();
            massProductReportDefinition.items = [];

            const newReportDefinitionItem = new ReportDefinitionItem();
            newReportDefinitionItem.type = ReportItemType.Checks;
            newReportDefinitionItem.checks = [];
            const newCheckItem: CheckItem = new CheckItem();

            newReportDefinitionItem.checks.push(newCheckItem);
            massProductReportDefinition.items.push(newReportDefinitionItem);

            await this.addNewRowAsync(massProductReportDefinition);
        }
    }

    public async duplicateMassProductReportDefinitionAsync(cell: CellModel<MassProductReportDefinition>): Promise<void> {
        await this.collapseAllRowsAsync();

        const copyModel: MassProductReportDefinition = Utility.clone(cell.model);

        copyModel.id = "";
        copyModel.massProducts = [];
        copyModel.items[0].checks
            ?.map(item => {
                item.pictures = null;
            });

        await this.addNewRowAsync(copyModel);

        // Modify name after the new row is added to set row as modified.
        copyModel.name += ` (${Localizer.genericDuplicate})`;
    }

    public async addNewRowAsync(model: MassProductReportDefinition): Promise<void> {
        const newRows: RowModel<MassProductReportDefinition>[] = await this.massProductReportDefinitionsGrid.insertAsync(0, model);

        const newRow: RowModel<MassProductReportDefinition> = newRows[0];
        const nameCell: CellModel<MassProductReportDefinition> = newRow.get("name");

        await nameCell.editAsync(true);

        newRow.expanded = true;

        await newRow.reRenderAsync();
    }

    public async collapseAllRowsAsync(): Promise<void> {
        const rowsToCollapse = this.massProductReportDefinitionsGrid
            .rows
            .where(row => row.expanded);

        await rowsToCollapse.forEachAsync(async (row) => {
            row.expanded = false;
            await row.reRenderAsync();
        });
    }

    public async handleInnerGridChangesAsync(row: RowModel<MassProductReportDefinition>): Promise<void> {
        const innerGrid: GridModel<CheckItem> | null = this.getCheckGrid(row);

        if (innerGrid != null) {

            const modifiedRows: RowModel<CheckItem>[] = innerGrid.rows.where(item => item.modified);
            const newRows: RowModel<CheckItem>[] = innerGrid.rows.where(item => !item.model.id);
            const deletedRows: RowModel<CheckItem>[] = innerGrid.rows.where(item => item.deleted);

            const checks: CheckItem[] | null = row.model.items[0].checks;

            await modifiedRows.forEachAsync(item => item.cancelAsync());
            await deletedRows.forEachAsync(item => item.setDeletedAsync(false));
            await newRows.forEachAsync(async innerRow => {
                checks?.removeAt(innerRow.index);
                await innerRow.grid.deleteAsync(innerRow.index);
            });

            this.initRow(row);
        }
    }

    private async onChangeSearchTermAsync(searchTerm: string | null): Promise<void> {
        if (this.state.searchTerm !== searchTerm) {
            await this.setState({searchTerm: searchTerm});

            this._searchDebouncer.debounce(
                async () => {
                    await this.reloadAsync();
                },
                500
            );
        }
    }

    private async setShowDeletedAsync(showDeleted: boolean): Promise<void> {
        await this.setState({showDeleted});
        await this.reloadAsync();
    }

    public async loadDataAsync(
        pageNumber: number,
        pageSize: number,
        sortColumnName: string | null,
        sortDirection: SortDirection | null
    ): Promise<IPagedList<MassProductReportDefinition>> {

        await this.getMassProductsAsync();

        const request = new ListMassProductReportDefinitionsRequest();
        request.pageNumber = pageNumber;
        request.pageSize = pageSize;
        request.showDeleted = this.state.showDeleted;
        request.searchTerm = this.state.searchTerm;
        request.sortColumnName = sortColumnName;
        request.sortDirection = sortDirection;

        return await RentaToolsController.listMassProductReportDefinitionsAsync(request);
    }

    public async getMassProductsAsync(): Promise<void> {
        const massProductsRequest = new GetMassProductsRequest();
        massProductsRequest.showDeleted = false;
        massProductsRequest.excludeUnMapped = false;

        const allMassProducts: MassProduct[] = await MassProductsController.getMassProductsAsync(massProductsRequest);

        await this.setState({allMassProducts});
    }

    public async reloadAsync(): Promise<void> {
        await this.massProductReportDefinitionsGrid.reloadAsync();
    }

    private renderDetailsContent(row: RowModel<MassProductReportDefinition>): React.ReactNode {
        const ref: React.RefObject<MassProductReportDefinitionDetails> = (row.tag as React.RefObject<MassProductReportDefinitionDetails>) ?? (row.tag = React.createRef());

        return (
            <>
                {
                    (row.model) &&
                    (
                        <MassProductReportDefinitionDetails id={`${row.index}_massProductReportDefinitionDetails`}
                                                            key={`${row.key}_massProductReportDefinitionDetails`}
                                                            ref={ref}
                                                            parentRow={row}
                                                            isDeleted={row.model.isDeleted}
                                                            onChange={async (_, updatedRow) => this.onInnerGridChangeAsync(updatedRow)}
                        />
                    )
                }
            </>
        );
    }

    public render(): React.ReactNode {
        return (
            <div className={styles.massProductsMappingsData}>

                <ToolbarContainer className={styles.toolbar}>

                    <ToolbarRow justify={JustifyContent.SpaceBetween}>

                        <TextInput clearButton
                                   width={"60ch"}
                                   value={this.state.searchTerm}
                                   placeholder={Localizer.massProductsMappingsDataSearch}
                                   onChange={(_, value) => this.onChangeSearchTermAsync(value)}
                        />

                        <Inline>
                            <Checkbox inline
                                      id="showDeletedMassProductReportDefinitions"
                                      label={Localizer.genericShowDeleted}
                                      value={this.state.showDeleted}
                                      onChange={async (_, value) => await this.setShowDeletedAsync(value)}
                            />

                            <Button id="reloadMassProductReportDefinitions"
                                    className="ml-1"
                                    title={Localizer.genericReload}
                                    icon={{name: "far history", size: IconSize.Large}}
                                    type={ButtonType.Info}
                                    onClick={async () => await this.reloadAsync()}
                            />

                            <Button id="addMassProductReportDefinition"
                                    icon={{name: "plus", size: IconSize.Large}}
                                    type={ButtonType.Orange}
                                    title={Localizer.massProductsMappingsDataAdd}
                                    onClick={async () => await this.addMassProductReportDefinitionAsync()}
                            />

                        </Inline>

                    </ToolbarRow>

                </ToolbarContainer>

                <Grid version2Styles optimization autoToggle
                      id={`${ch.getComponentId()}_massProductReportDefinitionsGrid`}
                      key={"massProductReportDefinitionsGrid"}
                      ref={this._massProductReportDefinitionsGridRef}
                      className={styles.massProductReportDefinitionsGrid}
                      pagination={10}
                      hovering={GridHoveringType.EditableCell}
                      odd={GridOddType.Row}
                      columns={this._massProductReportDefinitionsColumns}
                      noDataText={Localizer.genericNoData}
                      initRow={(row) => this.initRow(row)}
                      fetchData={async (_, pageNumber, pageSize, sortColumnName, sortDirection) => await this.loadDataAsync(pageNumber, pageSize, sortColumnName, sortDirection)}
                      renderDetails={(row) => this.renderDetailsContent(row)}
                />

            </div>
        );
    }
}